import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { ThemeEmitterComponent } from '../theme-emitter/theme-emitter.component';
import { Router } from '@angular/router';
import { DataGridComponent } from '../data-grid/data-grid.component';
import { PrintService } from 'app/shared/services/print.service';
import { AuthService } from 'app/shared/services/auth.service';
import { Utilities } from 'app/shared/helpers/utilities';
import { environment } from 'environments/environment';
import { LinkedLocation } from 'app/shared/models/subject';

@Component({
  selector: 'app-datagrid',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  animations: fuseAnimations,
})
export class ListComponent implements OnInit, AfterViewInit {

  initialColor = '#fff';
  contrastColor = '#000';

  _rows: any;
  _columns: any[];
  _color = this.initialColor;
  _title: string;

  showFilter = false;
  hasMultipleRowsSelected = false;
  hasSingleRowSelected = false;
  canArchive = false;
  showBatchReAssign = false;
  enableShowOnMap: boolean = false;
  userReadOnly = false;

  @Input() set rows(val: any) {
    if (val) {
      if ((val.length > 0) && val[0].id) {
        val = val.sort((a, b) => b.id - a.id);
        // if the objects being passed in have an archived property available then they can potentially be archived
        // archived records are in their own list so test the first item archived property to determine if we are in an archived list or not
        // if not in archived list and has an archived property then we canArchive
        this.canArchive = val[0].hasOwnProperty('archived') && (val[0].isPrimary) && (!val[0].archived);
      }
      this._rows = [...val];
    }
  }

  @Input() set columns(val: any) {
    if (val && (val.length > 0)) {
      this._columns = [...val];
    }
  }

  @Input() set color(val: string) {
    this._color = val;
    this.contrastColor = Utilities.contrastColour(val);
  }

  @Input() set title(val: string) {
    this._title = val;

    // save any existing state before we change the storage key
    if (this.dataGrid && this.dataGrid.instance && this.storageKey) {
      this.dataGrid.saveState(this.dataGrid.instance.state());
    }

    // if (val && (!this.storageKey)){
    if (val) {
      this.storageKey = val.replace(/\s/g, '').toLowerCase() + '.list';
    }
  }

  @Input() allowBatchReAssign = false;

  @Input() allowAdd = true;
  @Input() allowDelete = true;
  @Input() allowEdit = true;
  @Input() addRouterLink: string;
  @Input() keyProperty: string;
  @Input() showPurge = false;
  @Input() detailTemplate: string;
  @Input() selectionMode = 'single';
  @Input() showLoading = false;
  @Input() storageKey = '';
  @Input() type = '';

  @Output() onBatchReAssign: EventEmitter<any[]> = new EventEmitter();
  @Output() onShowOnMap: EventEmitter<any[]> = new EventEmitter();
  @Output() onAdd: EventEmitter<any> = new EventEmitter();
  @Output() onDelete: EventEmitter<any> = new EventEmitter();
  @Output() onEdit: EventEmitter<any> = new EventEmitter();
  @Output() onOpenLink: EventEmitter<any> = new EventEmitter();
  @Output() onPurge: EventEmitter<any> = new EventEmitter();
  @Output() onSelectionChanged: EventEmitter<any> = new EventEmitter();
  @Output() onRowUpdated: EventEmitter<any> = new EventEmitter();
  @Output() onArchiveSelectedRows: EventEmitter<any> = new EventEmitter();
  @Output() onDeleteSelectedRows: EventEmitter<any> = new EventEmitter();

  @ViewChild(ThemeEmitterComponent) theme: ThemeEmitterComponent;
  @ViewChild(DataGridComponent) dataGrid: DataGridComponent;
  @ViewChild('content') content: ElementRef;

  constructor(private router: Router, private printService: PrintService, private authService: AuthService) { }

  ngOnInit() {
    this.userReadOnly = this.authService.isUserReadOnly(); // hasPriv('readonly');
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (!this._color || (this._color === this.initialColor)) {
        this._color = this.theme.accentColor;
        this.contrastColor = Utilities.contrastColour(this._color);
      }
    }, 0);
  }

  selectionChanged(e) {
    this.hasMultipleRowsSelected = (e && (e.selectedRowKeys.length > 1));
    this.hasSingleRowSelected = (e && (e.selectedRowKeys.length == 1));
    this.showBatchReAssign = (e.selectedRowKeys.length > 0 && this.allowBatchReAssign);

    var cnt = 0;
    var cnt2=0;
    if (e.selectedRowsData && e.selectedRowsData.length > 0) {
      cnt = e.selectedRowsData.filter(i=>i.hasLocation===true).length  || 0;      
      cnt2 = e.selectedRowsData.filter(i=>i.linkedLocations && i.linkedLocations.length>0).length  || 0;      
    }
    this.enableShowOnMap = ((cnt > 0) || (cnt2>0)) && (environment.enableGeoLocation===true);

    this.onSelectionChanged.next(e);
  }

  rowUpdated(e) {
    this.onRowUpdated.next(e);
  }

  add() {
    this.dataGrid.instance.hideColumnChooser();
    if (this.addRouterLink) {
      this.router.navigateByUrl(this.addRouterLink);
    }
    else {
      this.onAdd.next(undefined);
    }
  }

  edit(row) {
    this.onEdit.next(row);
  }

  delete(row) {
    this.onDelete.emit(row);
  }

  openLink(link) {
    this.onOpenLink.next(link);
  }

  archiveSelectedRows() {
    const selectedKeys = this.dataGrid.instance.getSelectedRowKeys();
    this.onArchiveSelectedRows.next(selectedKeys);
  }

  deleteSelectedRows() {
    const selectedKeys = this.dataGrid.instance.getSelectedRowKeys();
    this.onDeleteSelectedRows.next(selectedKeys);
  }

  filterVisibilityChanged(visible) {
    this.showFilter = visible;
  }

  purge() {
    this.onPurge.next(undefined);
  }

  toggleFilterRow() {
    this.dataGrid.toggleFilterRow();
  }

  export() {
    this.dataGrid.export(`${this._title.replace(/\s/g, '')}.xlsx`);
  }

  expandAllRows() {
    this.dataGrid.expand(true);
  }

  collapseAllRows() {
    this.dataGrid.expand(false);
  }

  reset() {
    this.dataGrid.reset();
  }

  refresh() {
    if (this.dataGrid && this.dataGrid.instance) {
      this.dataGrid.instance.clearFilter();
      this.dataGrid.instance.repaint();
    }
  }

  print() {
    const html = this.content.nativeElement.innerHTML;
    const css = '<link rel="stylesheet" type="text/css" href="https://cdn3.devexpress.com/jslib/20.1.4/css/dx.common.css" async /> ' +
      '<link rel="stylesheet" type="text/css" href="https://cdn3.devexpress.com/jslib/20.1.4/css/dx.light.css" async />';
    this.printService.printHtml(this._title, html, css, true);
  }

  batchReAssign() {
    const selectedCases = this.dataGrid.instance.getSelectedRowsData();
    const selectedCaseUIDs: any = [];
    selectedCases.forEach((i) => selectedCaseUIDs.push(i.uid));
    this.onBatchReAssign.emit(selectedCaseUIDs);
  }

  showOnMap(): void {
    const selectedCases = this.dataGrid.instance.getSelectedRowsData();

    var q = selectedCases.filter(i=>i.hasLocation==true && i.latitude && i.longitude);
    if (q.length>0)
    {
      var q2:LinkedLocation[] = [];
      selectedCases.filter(i=>i.hasLocation==true && i.latitude && i.longitude).forEach(i=>{
        q2.push(new LinkedLocation(i));
      });
      this.onShowOnMap.emit(q2);
    }
    else 
    {
      const linkedLocations = selectedCases.flatMap(row => row.linkedLocations);      
      this.onShowOnMap.emit(linkedLocations);    
    }
  }
}
